import { useMemo } from "react";

import moment from "moment";

import { getIsSubmissionPeriodOpen, useGetDataSubmissionDates, useGetFarmDataSubmissionStatus, useGetFarms, useGetVerificationEditingRequests } from "utils";

import { FarmDataSubmissionStatusResponse } from "models";

type SubmissionStatus = {
  isDataSubmitted: boolean;
  isDataSubmissionReopened: boolean;
  isSubmissionPeriod: boolean;
  isSubmissionFeatureEnabled: boolean;
  isBeforeQ1EndOfNextYear: boolean;
  submissionStatusValue?: FarmDataSubmissionStatusResponse;
};

const SUBMISSION_UI_CONFIG = {
  Q1_END_MONTH: 2, // March (0-indexed) blame js haha!
  Q1_END_DAY: 31,
} as const;

/**
 * Determines if the submission UI should be shown based on:
 * - Submission status from API
 * - Submission Feature flag — 🚨 TODO:  I guess are getting rid of these at some point
 * - Current date (before Q1 end of reporting year + 1)
 * - Field enrollment status (optional) or inventory program — all fields are enrolled in the inventory program
 */
export const useSubmissionStatus = (reportingYear?: number, fieldIsEnrolled?: boolean): SubmissionStatus => {
  const { currentFarm } = useGetFarms();
  const { data: dataSubmissionDates } = useGetDataSubmissionDates({ farmId: currentFarm?.id });
  const { data: submissionStatusValue } = useGetFarmDataSubmissionStatus({
    farmId: currentFarm?.id ?? "",
  });

  const { data: verificationEditingRequests } = useGetVerificationEditingRequests({
    farmId: currentFarm?.id ?? "",
  });

  const isSubmissionFeatureEnabled = !!currentFarm?.carbonProgram?.platformFeatures?.fieldManagement?.submission;

  const isBeforeQ1EndOfNextYear = useMemo(() => {
    if (!reportingYear) return false;

    const q1EndOfNextYear = moment()
      .set({
        year: reportingYear + 1,
        month: SUBMISSION_UI_CONFIG.Q1_END_MONTH,
        date: SUBMISSION_UI_CONFIG.Q1_END_DAY,
      })
      .endOf("day");

    return moment().isBefore(q1EndOfNextYear);
  }, [reportingYear]);

  const isDataSubmitted = useMemo(() => {
    const baseConditions = !!submissionStatusValue?.isSubmitted && isSubmissionFeatureEnabled && isBeforeQ1EndOfNextYear;

    if (fieldIsEnrolled !== undefined) {
      return baseConditions && fieldIsEnrolled;
    }

    return baseConditions;
  }, [submissionStatusValue?.isSubmitted, isSubmissionFeatureEnabled, isBeforeQ1EndOfNextYear, fieldIsEnrolled]);

  const hasOpenVerificationEditingRequests = useMemo(
    () =>
      (verificationEditingRequests &&
        verificationEditingRequests.length > 0 &&
        verificationEditingRequests.some((request) => request.verificationYear === reportingYear && request.completedAt === null)) ??
      false,
    [reportingYear, verificationEditingRequests]
  );

  const isDataSubmissionReopened = useMemo(
    () =>
      (isDataSubmitted &&
        (hasOpenVerificationEditingRequests ||
          verificationEditingRequests?.some(
            (request) => request.verificationYear === reportingYear && moment(request.createdAt).isAfter(moment(submissionStatusValue?.lastSubmissionDate))
          ))) ??
      false,
    [isDataSubmitted, hasOpenVerificationEditingRequests, reportingYear, verificationEditingRequests, submissionStatusValue?.lastSubmissionDate]
  );

  const isSubmissionPeriod = useMemo(
    () => getIsSubmissionPeriodOpen(dataSubmissionDates?.submissionStartDate, dataSubmissionDates?.submissionEndDate),
    [dataSubmissionDates?.submissionEndDate, dataSubmissionDates?.submissionStartDate]
  );

  return {
    isDataSubmitted,
    isDataSubmissionReopened,
    isSubmissionPeriod,
    isSubmissionFeatureEnabled,
    submissionStatusValue,
    isBeforeQ1EndOfNextYear,
  };
};
